/**
 * Detalle de campaña: datos + lista de avisos. Permite agregar un aviso
 * (precargando la campaña en la sección Avisos) y entrar a cada aviso.
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Megaphone, ChevronRight, Calendar, Building2,
  Trash2, AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useMe } from "@/hooks/use-me";
import { media, AD_TYPE_LABELS, type AdType } from "@/lib/api";

export const Route = createFileRoute("/_auth/media/campaigns_/$campaignId")({
  component: CampaignDetail,
});

function CampaignDetail() {
  const { campaignId } = Route.useParams();
  const id = Number(campaignId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const canDelete = me?.role === "owner" || me?.role === "admin" || me?.role === "superadmin";
  const [confirmOpen, setConfirmOpen] = useState(false);

  const campaignQ = useQuery({ queryKey: ["media", "campaign", id], queryFn: () => media.getCampaign(id) });
  const adsQ = useQuery({ queryKey: ["media", "ads", "by_campaign", id], queryFn: () => media.listAds({ campaign_id: id, limit: 500 }) });
  const clientsQ = useQuery({ queryKey: ["media", "clients"], queryFn: () => media.listClients({ limit: 200 }) });

  // Impacto del borrado — solo se consulta al abrir la alerta.
  const impactQ = useQuery({
    queryKey: ["media", "campaign", id, "delete-impact"],
    queryFn: () => media.campaignDeleteImpact(id),
    enabled: confirmOpen,
  });

  const del = useMutation({
    mutationFn: () => media.deleteCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "campaigns"] });
      qc.invalidateQueries({ queryKey: ["media", "ads"] });
      qc.invalidateQueries({ queryKey: ["media", "reports"] });
      toast.success("Campaña eliminada");
      navigate({ to: "/media/campaigns" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (campaignQ.isLoading) return <div className="px-6 py-6 max-w-4xl mx-auto"><Skeleton className="h-64" /></div>;
  if (!campaignQ.data) return <div className="px-6 py-6">Campaña no encontrada</div>;
  const c = campaignQ.data;
  const clientName = (clientsQ.data ?? []).find(x => x.id === c.client_id)?.name ?? `Cliente #${c.client_id}`;
  const ads = adsQ.data ?? [];

  return (
    <div className="px-6 py-6 max-w-4xl mx-auto">
      <Link to="/media/campaigns" className="text-xs text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Volver a campañas
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-ink-900 dark:text-white">{c.name}</h1>
            {c.code && <Badge variant="outline" className="font-mono text-[10px]">{c.code}</Badge>}
            <Badge variant={c.status === "activa" ? "default" : "outline"}>{c.status}</Badge>
          </div>
          <div className="text-xs text-ink-500 mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Building2 size={12} /> {clientName}</span>
            {c.period_start && c.period_end && (
              <span className="inline-flex items-center gap-1"><Calendar size={12} /> {c.period_start} → {c.period_end}</span>
            )}
            {c.budget_amount != null && (
              <span>· {c.currency} {Number(c.budget_amount).toLocaleString("es-PE")}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={() => navigate({ to: "/media/ads", search: { campaign: id } })}>
            <Plus size={14} /> Agregar aviso
          </Button>
          {canDelete && (
            <Button variant="destructive" title="Eliminar campaña" onClick={() => setConfirmOpen(true)}>
              <Trash2 size={14} /> Eliminar
            </Button>
          )}
        </div>
      </div>

      {c.description && (
        <p className="text-sm text-ink-600 dark:text-ink-300 mb-5">{c.description}</p>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Avisos ({ads.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {adsQ.isLoading ? <Skeleton className="h-24" /> : (
            <>
              {ads.map(a => (
                <Link key={a.id} to="/media/ads/$adId" params={{ adId: String(a.id) }} className="block">
                  <div className="border border-ink-200 dark:border-ink-700 rounded-lg p-3 hover:border-brand-300 hover:shadow-sm transition-all flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
                      <Megaphone size={16} className="text-brand-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{a.title}</span>
                        <Badge variant="outline" className="font-mono text-[10px]">{a.code}</Badge>
                      </div>
                      <div className="text-xs text-ink-500 mt-0.5">{AD_TYPE_LABELS[a.ad_type as AdType] ?? a.ad_type}</div>
                    </div>
                    <ChevronRight size={16} className="text-ink-400 shrink-0" />
                  </div>
                </Link>
              ))}
              {!ads.length && (
                <div className="text-center py-8 text-ink-500 text-sm border-2 border-dashed border-ink-200 rounded-lg">
                  Esta campaña aún no tiene avisos. Agrega el primero.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!del.isPending) setConfirmOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-bad">
              <AlertTriangle size={18} /> Eliminar campaña — acción irreversible
            </DialogTitle>
            <DialogDescription>
              Se eliminará <strong>{c.name}</strong> y <strong>toda su información asociada
              de forma permanente</strong>. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-bad/30 bg-bad/5 p-3 text-sm">
            {impactQ.isLoading ? (
              <Skeleton className="h-24" />
            ) : impactQ.data ? (
              <>
                <p className="font-semibold text-ink-900 dark:text-white mb-2">Se borrará:</p>
                <ul className="space-y-1 text-ink-600 dark:text-ink-300">
                  <li>· {impactQ.data.ads} aviso(s)</li>
                  <li>· {impactQ.data.placements} placement(s)</li>
                  <li>· {impactQ.data.evidences} adjunto(s) de inserciones</li>
                  <li>· {impactQ.data.reports} reporte(s) propios de esta campaña</li>
                  {(impactQ.data.report_assets > 0 || impactQ.data.report_indicators > 0) && (
                    <li>· {impactQ.data.report_assets} adjunto(s) y {impactQ.data.report_indicators} indicador(es)
                      ligados a esta campaña en reportes a nivel cliente</li>
                  )}
                </ul>
                <p className="text-[11px] text-ink-500 mt-2">
                  Los reportes a nivel cliente (no específicos de esta campaña) se conservan.
                </p>
              </>
            ) : (
              <p className="text-ink-500">No se pudo calcular el impacto.</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={del.isPending}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending || impactQ.isLoading}>
              <Trash2 size={14} /> {del.isPending ? "Eliminando…" : "Eliminar definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
